<template>
  <div v-if="selectedFiles.length > 0" class="selection-bar" role="region" aria-label="ไฟล์ที่เลือก">
    <div class="selection-row">
      <span class="selection-bubble">{{ selectedFiles.length }}</span>
      <span class="selection-count">{{ unit }}ที่เลือก</span>
      <button
        v-if="selectAllLabel"
        type="button"
        class="selection-link"
        @click="$emit('select-all')"
      >
        {{ selectAllLabel }}
      </button>
      <button type="button" class="selection-link" @click="$emit('clear')">
        ล้างการเลือก
      </button>
    </div>
    <div class="selection-actions">
      <button type="button" class="selection-btn download" @click="$emit('bulk-download')">
        <i class="fas fa-download" aria-hidden="true"></i>
        <span class="label-wide">{{ selectedFiles.length > 1 ? "ดาวน์โหลด .zip" : "ดาวน์โหลด" }}</span>
        <span class="label-narrow">บันทึกลงเครื่อง</span>
      </button>
      <button
        v-if="canShare"
        type="button"
        class="selection-btn share"
        :class="{ ready: shareReady }"
        @click="$emit('share')"
      >
        <i class="fas fa-share-square" aria-hidden="true"></i>
        {{ shareReady ? "แตะเพื่อแชร์" : "แชร์" }}
      </button>
      <button
        type="button"
        class="selection-btn delete"
        :aria-label="`ลบ${unit}ที่เลือก`"
        @click="$emit('bulk-delete')"
      >
        <i class="fas fa-trash-alt" aria-hidden="true"></i>
        <span class="label-narrow">ลบ</span>
      </button>
    </div>
    <div v-if="notice" class="selection-notice" role="status">
      <i class="fas fa-check" aria-hidden="true"></i>
      {{ notice }} · ยังเลือกไว้ ทำต่อได้เลย
    </div>
  </div>
</template>

<script>
/**
 * Bar for the files currently selected: select the rest of their category,
 * clear, download (a zip when there are several), share (phones) and
 * delete. It stays after a download so the same selection can be used
 * again. On phones it spans the bottom with large buttons.
 */
export default {
  name: "GalleryActions",
  props: {
    selectedFiles: {
      type: Array,
      default: () => [],
    },
    // "เลือกทั้งหมดใน Intra-Oral (18)"; hidden when null
    selectAllLabel: {
      type: String,
      default: null,
    },
    notice: {
      type: String,
      default: "",
    },
    unit: {
      type: String,
      default: "ไฟล์",
    },
    // the device can hand files to its share sheet
    canShare: {
      type: Boolean,
      default: false,
    },
    // files are fetched and waiting for a tap to open the share sheet
    shareReady: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["bulk-download", "bulk-delete", "select-all", "clear", "share"],
};
</script>

<style scoped>
.selection-bar {
  position: fixed;
  left: 50%;
  bottom: 1.5rem;
  transform: translateX(-50%);
  z-index: 1060;
  max-width: calc(100vw - 2rem);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem 0.5rem;
  padding: 0.5rem 0.5rem 0.5rem 0.625rem;
  border-radius: 28px;
  background: #101828;
  color: #fff;
  box-shadow: 0 16px 40px rgba(16, 24, 40, 0.35);
  animation: slideUp 0.2s ease-out;
}
.selection-row,
.selection-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.selection-actions {
  padding-left: 0.5rem;
  border-left: 1px solid #3b4350;
}
.selection-bubble {
  min-width: 30px;
  height: 30px;
  padding: 0 0.375rem;
  border-radius: 15px;
  background: var(--primary, #0b63c5);
  font-size: 0.8125rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.selection-count {
  font-weight: 600;
  padding: 0 0.375rem 0 0.125rem;
  white-space: nowrap;
}
.selection-link {
  min-height: 38px;
  padding: 0 0.625rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #bcd6f7;
  font-size: 0.875rem;
  white-space: nowrap;
}
.selection-link:hover {
  background: rgba(255, 255, 255, 0.08);
}
.selection-btn {
  min-height: 38px;
  padding: 0 1rem;
  border: 0;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  white-space: nowrap;
}
.selection-btn.download {
  background: #fff;
  color: #14181f;
  font-weight: 600;
}
.selection-btn.share {
  background: #26303d;
  color: #fff;
}
.selection-btn.share.ready {
  background: var(--primary, #0b63c5);
  font-weight: 600;
}
.selection-btn.delete {
  width: 38px;
  padding: 0;
  justify-content: center;
  background: #3a1f1d;
  color: #ffb4ab;
}
.label-narrow {
  display: none;
}
.selection-notice {
  flex-basis: 100%;
  padding: 0 0 0.125rem 2.5rem;
  font-size: 0.75rem;
  color: #9fd8b4;
}

@media (max-width: 575.98px) {
  .selection-bar {
    left: 0;
    right: 0;
    bottom: 0;
    transform: none;
    max-width: none;
    border-radius: 20px 20px 0 0;
    padding: 0.5rem 0.75rem calc(0.75rem + env(safe-area-inset-bottom));
    flex-direction: column;
    align-items: stretch;
    animation: none;
  }
  .selection-row {
    justify-content: flex-start;
  }
  .selection-notice {
    padding-left: 0;
  }
  .selection-actions {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
    gap: 0.5rem;
    padding: 0;
    border: 0;
  }
  .selection-btn,
  .selection-btn.delete {
    width: auto;
    min-height: 56px;
    border-radius: 14px;
    flex-direction: column;
    justify-content: center;
    gap: 0.25rem;
    font-size: 0.8125rem;
  }
  .selection-btn i {
    font-size: 1.125rem;
  }
  .label-wide {
    display: none;
  }
  .label-narrow {
    display: inline;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translate(-50%, 12px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}
</style>
